import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismCard, PrismIcon } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import GeneralSmartInsightsBadgePriority from 'lumen/components/general/smart-insights/badge-priority';
import type { SmartInsightDay } from 'lumen/models';
import getElement from 'lumen/modifiers/get-element';

import styles from './list-card-smart-insights.module.css';

interface GeneralSmartInsightsListListCardSmartInsightsSignature {
  Args: {
    selectSmartInsightsDay: (day: SmartInsightDay) => void;
    smartInsightDay: SmartInsightDay;
  };
}

export default class GeneralSmartInsightsListListCardSmartInsights extends Component<GeneralSmartInsightsListListCardSmartInsightsSignature> {
  @tracked cardContentElement: HTMLElement | null = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedStayDate() {
    const { smartInsightDay } = this.args;

    return dayjs(smartInsightDay.stayDate).format('ddd, MMM D, YYYY');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showBottomOverlay() {
    const { cardContentElement } = this;

    if (!cardContentElement) {
      return false;
    }

    return cardContentElement.scrollHeight > cardContentElement.clientHeight;
  }

  @action onClickViewDetails(): void {
    const { selectSmartInsightsDay, smartInsightDay } = this.args;

    selectSmartInsightsDay(smartInsightDay);
  }

  @action setCardContentElement(element: HTMLElement): void {
    this.cardContentElement = element;
  }

  <template>
    <div
      class="{{styles.card}} list-card-smart-insights flex flex-col gap-200"
      data-test-id="list-card-smart-insights"
    >
      <h3 data-test-id="list-card-date">{{this.formattedStayDate}}</h3>

      <PrismCard @contentLayout="noPadding">
        <:header>
          <GeneralSmartInsightsBadgePriority
            @priority={{@smartInsightDay.stayDatePriority}}
          />
        </:header>

        <:content>
          <div
            class="list-card-smart-insights__content flex flex-col gap-500 overflow-hidden px-400 pt-200 pb-600"
            data-test-id="list-card-content"
            {{getElement this.setCardContentElement}}
          >
            {{#each
              @smartInsightDay.insightEntriesSortedByRelevance
              as |SmartInsightDayInsightEntry|
            }}
              <div class="flex flex-row gap-400" data-test-id="list-card-entry">
                <div class="list-card-smart-insights__icon-container">
                  <PrismIcon
                    @color="on-intelligence"
                    @name={{SmartInsightDayInsightEntry.iconName}}
                    @size="small"
                    data-test-id="list-card-entry-icon"
                  />
                </div>

                <div class="flex flex-col gap-100">
                  <div class="flex flex-col">
                    <p class="text-300--regular text-neutral-default">
                      {{htmlSafe SmartInsightDayInsightEntry.formattedText}}
                    </p>
                  </div>
                </div>
              </div>
            {{/each}}

            {{#if this.showBottomOverlay}}
              <div
                class="list-card-smart-insights__bottom-overlay"
                data-test-id="list-card-bottom-overlay"
              />
            {{/if}}
          </div>
        </:content>

        <:footer>
          <div class="flex flex-row justify-end">
            <PrismButton
              @buttonType="ghost"
              @iconAfter="arrow-right"
              @label={{t "smart_insights.view_details"}}
              @onClick={{this.onClickViewDetails}}
              data-test-id="view-details-button"
            />
          </div>
        </:footer>
      </PrismCard>
    </div>
  </template>
}
